'use client'
import { useCallback } from 'react'
import cn from 'classnames'
import { Swiper, SwiperSlide } from 'swiper/react'
import { IconName } from '@/icons'
import { FreeMode } from 'swiper/modules'

import 'swiper/css'
import 'swiper/css/free-mode'

import styles from './Tabs.module.scss'

export interface ITab<TValue> {
  value: TValue
  title: string
  isDisabled?: boolean
  styles?: any
  icon?: IconName
}

interface ITabsProps<TValue> {
  items: ITab<TValue>[]
  value: TValue
  onChange: (value: TValue) => void
  className?: string
  classNameItem?: string
}
export const Tabs = <TValue,>({
  items,
  onChange,
  value,
  className,
  classNameItem,
}: ITabsProps<TValue>) => {
  return (
    <Swiper
      spaceBetween={0}
      slidesPerView={'auto'}
      modules={[FreeMode]}
      freeMode={true}
      className={cn(className)}
      wrapperClass={styles.container}
      slidesOffsetAfter={20}
      slidesOffsetBefore={20}
      breakpoints={{
        480: {
          slidesOffsetAfter: 0,
          slidesOffsetBefore: 0,
        },
        768: {
          spaceBetween: 0,
        },
        1024: {
          enabled: false,
        },
      }}
    >
      {items.map((item) => (
        <SwiperSlide key={String(item.value)} className={styles.slide}>
          <Tab
            value={item.value}
            title={item.title}
            isActive={item.value === value}
            isDisabled={item.isDisabled}
            mixStyles={item.styles}
            onClick={onChange}
            className={classNameItem}
          />
        </SwiperSlide>
      ))}
    </Swiper>
  )
}

interface ITabProps<TValue> {
  value: TValue
  title: string
  isActive: boolean
  isDisabled?: boolean
  mixStyles?: any
  className?: string
  onClick: (value: TValue) => void
}
const Tab = <TValue,>({
  value,
  title,
  onClick,
  isActive,
  isDisabled,
  className,
  mixStyles,
}: ITabProps<TValue>) => {
  const click = useCallback(() => {
    if (!isDisabled) {
      onClick(value)
    }
  }, [value, onClick, isDisabled])

  return (
    <button
      className={cn(styles.tab, className, {
        [styles.isActive]: isActive,
        [styles.isDisabled]: isDisabled,
      })}
      style={mixStyles}
      onClick={click}
    >
      {title}
    </button>
  )
}
